<template>
  <div id="subscription">
    <HeaderMenuComponent 
      title="Suscripción"
      :permissions="permissions" 
      :subscription="userData?.subscription"
      :isMobile="isMobile"
      @toggle-sidebar="toggleSidebar"
      @logout="logout"
      @show-toast="showToast" />

    <div class="subscription-window">
      <div class="subscription-container" v-if="userData">
        <div class="subscription-section intro-section">
          <div class="intro-content">
            <h2>🧑🏽‍🤝‍🧑🏽 ChatPTS es un esfuerzo colectivo y militante del PTS y La Izquierda Diario</h2>
            <p>🫂 No puede funcionar sin tu aporte, ya que para responder cada consulta gastamos dinero</p>
            <p>💲 No recibimos financiación de ningún tipo</p>
          </div>
        </div>

        <div class="subscription-section current-plan">
          <h2>Tu Plan Actual</h2>

          <div v-if="userData.subscription && isValidSubscription(userData.subscription)" class="current-subscription">
            <div class="subscription-card">
              <div class="subscription-row">
                <div class="subscription-label">Plan:</div>
                <div class="subscription-value">{{ getSubscriptionName(userData.subscription) }}</div>
              </div>
              <div class="subscription-row">
                <div class="subscription-label">Estado:</div>
                <div class="subscription-value">{{ getSubscriptionStatus(userData.subscription) }}</div>
              </div>
              <div class="subscription-row" v-if="userData.subscription.created_at">
                <div class="subscription-label">Desde:</div>
                <div class="subscription-value">{{ formatDate(userData.subscription.created_at) }}</div>
              </div>
              <div class="subscription-row" v-if="userData.subscription.valid_until">
                <div class="subscription-label">Válido hasta:</div>
                <div class="subscription-value">{{ formatDate(userData.subscription.valid_until) }}</div>
              </div>
            </div>
            <div class="subscription-benefits">
              <h3>Beneficios de tu plan:</h3>
              <ul>
                <li>ChatPTS ilimitado</li>
                <li>Acceso completo a todas las funcionalidades</li>
                <li>Apoyo directo al proyecto militante</li>
              </ul>
            </div>
          </div>

          <div v-else class="no-subscription">
            <p class="no-plan-message">No posees una suscripción activa.</p>
            <p class="free-plan-info">Actualmente estás usando el plan gratuito con recursos limitados.</p>
          </div>
        </div>

        <div class="subscription-section available-plans">
          <h2>Planes Disponibles</h2>

          <div class="plans-section">
            <h3>Suscripción mensual, con acceso ilimitado</h3>
            <div class="plans-grid monthly-plans">
              <div class="plan-card standard">
                <div class="plan-header">
                  <h4>Plan Básico</h4>
                  <div class="plan-price">$3.000<span>/mes</span></div>
                </div>
                <div class="plan-features">
                  <ul>
                    <li>Acceso ilimitado a ChatPTS</li>
                    <li>Todas las funcionalidades disponibles</li>
                  </ul>
                </div>
                <div class="plan-action">
                  <a class="plan-button" 
                     href="https://www.mercadopago.com.ar/subscriptions/checkout?preapproval_plan_id=2c938084922e71cf01923ae3508c03a4"
                     target="_blank">
                    ✨ SUSCRIBIRME ✨
                  </a>
                </div>
              </div>

              <div class="plan-card premium">
                <div class="plan-badge">Recomendado</div>
                <div class="plan-header">
                  <h4>Plan Solidario</h4>
                  <div class="plan-price">$6.000<span>/mes</span></div>
                </div>
                <div class="plan-features">
                  <ul>
                    <li>Acceso ilimitado a ChatPTS</li>
                    <li>Todas las funcionalidades disponibles</li>
                    <li>Aporte extra para sostener el proyecto</li>
                  </ul>
                </div>
                <div class="plan-action">
                  <a class="plan-button premium-button"
                     href="https://www.mercadopago.com.ar/subscriptions/checkout?preapproval_plan_id=2c938084922e71ec01923b00eaff03a7"
                     target="_blank">
                    ✨ SUSCRIBIRME ✨
                  </a>
                </div>
              </div>
            </div>

            <div class="separator">- ó -</div>

            <h3>Aporte único, válido por 30 días</h3>
            <div class="plans-grid unique-plans">
              <div class="plan-card unique">
                <div class="plan-header">
                  <h4>Aporte Único Básico</h4>
                  <div class="plan-price">$3.000<span>/30 días</span></div>
                </div>
                <div class="plan-features">
                  <ul>
                    <li>Acceso completo por 30 días</li>
                    <li>Todas las funcionalidades</li>
                  </ul>
                </div>
                <div class="plan-action">
                  <a class="plan-button unique-button" 
                     href="https://mpago.la/322zs5s"
                     target="_blank">
                    APORTAR $3.000
                  </a>
                </div>
              </div>

              <div class="plan-card unique">
                <div class="plan-header">
                  <h4>Aporte Único Solidario</h4>
                  <div class="plan-price">$6.000<span>/30 días</span></div>
                </div>
                <div class="plan-features">
                  <ul>
                    <li>Acceso completo por 30 días</li>
                    <li>Todas las funcionalidades</li>
                    <li>Aporte extra solidario</li>
                  </ul>
                </div>
                <div class="plan-action">
                  <a class="plan-button unique-button" 
                     href="https://mpago.la/26dsGmT"
                     target="_blank">
                    APORTAR $6.000
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="subscription-section paid-section">
          <h2>¿Ya pagaste tu suscripción?</h2>
          
          <div v-if="!showPaidForm" class="paid-intro">
            <p>Si ya realizaste el pago de tu suscripción a través de MercadoPago y aún no está asociado a tu usuario, podemos ayudarte a activarla.</p>
            <button class="show-form-button" @click="showPaidForm = true">
              YA PAGUÉ MI SUSCRIPCIÓN
            </button>
          </div>

          <div v-else class="paid-form">
            <p>👉🏽 Completá tus datos abajo para que podamos encontrar tu suscripción y activarla.</p>
            <div class="form-container">
              <div class="form-group">
                <label for="email">Email de la cuenta de MercadoPago o email usado en el pago:</label>
                <input type="email" 
                       id="email" 
                       v-model="mercadopagoEmail" 
                       @keyup.enter="buscarSuscripcion"
                       placeholder="tu@email.com" />
              </div>
              <div class="form-group">
                <label for="transaction_id">Número de operación de MercadoPago:</label>
                <input type="text" 
                       id="transaction_id" 
                       v-model="transactionId"
                       @keyup.enter="buscarSuscripcion"
                       placeholder="Encontralo en tu comprobante de pago" />
              </div>
              <div class="form-actions">
                <button class="search-button" @click="buscarSuscripcion" :disabled="!mercadopagoEmail || !transactionId">
                  BUSCAR SUSCRIPCIÓN
                </button>
                <button class="cancel-button" @click="showPaidForm = false">
                  Cancelar
                </button>
              </div>
            </div>
            <p class="help-text">
              En caso de que continúes con algún problema, por favor, 
              <a href="https://docs.google.com/forms/d/18aMGrwHl0ZLLWMgXbXfmLqWfwyIU42wYc15WlVfU9KY/edit" target="_blank">contactanos</a>
            </p>
          </div>
        </div>
      </div>

      <div v-else class="loading-subscription">
        <p>Cargando información de suscripción...</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import HeaderMenuComponent from './HeaderMenuComponent.vue';

axios.defaults.withCredentials = true;

export default {
  name: "SubscriptionComponent",
  components: {
    HeaderMenuComponent
  },
  props: {
    email: {
      type: String,
      required: true
    },
    permissions: {
      type: Array,
      required: false,
      default: () => []
    },
  },
  emits: ['show-toast', 'toggle-sidebar', 'logout'],
  data() {
    return {
      userData: null,
      isLoading: true,
      error: null,
      showPaidForm: false,
      mercadopagoEmail: '',
      transactionId: '',
      isMobile: false
    };
  },
  methods: {
    async fetchUserData() {
      try {
        this.isLoading = true;
        const response = await axios.get(process.env.VUE_APP_BASE_URL + '/api/v2/users/me/');
        this.userData = response.data;
      } catch (error) {
        console.error('Error al buscar datos del usuario:', error);
        this.error = 'No fue posible cargar los datos del usuario. Por favor, intenta nuevamente más tarde.';
        this.showToast('Error al cargar datos del usuario', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async buscarSuscripcion() {
      if (!this.mercadopagoEmail || !this.transactionId) return;

      try {
        const response = await axios.post(process.env.VUE_APP_BASE_URL + '/api/v2/suscripciones/buscar-suscripcion/', {
          mercadopago_email: this.mercadopagoEmail,
          transaction_id: this.transactionId
        });
        
        if (response.status === 200) {
          this.showToast('Suscripción encontrada y activada correctamente', 'success');
          // Refresh user data
          await this.fetchUserData();
          this.showPaidForm = false;
          this.mercadopagoEmail = '';
          this.transactionId = '';
        }
      } catch (error) {
        console.error(error);
        const detail = error.response ? error.response.data.detail : 'Error desconocido';
        this.showToast('Error al buscar la suscripción: ' + detail, 'error');
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    },

    isValidSubscription(subscription) {
      if (!subscription || !subscription.valid_until) return false;
      return new Date(subscription.valid_until) > new Date();
    },

    getSubscriptionName(subscription) {
      if (!subscription) return 'Sin suscripción';
      return subscription.subscription || 'Suscripción activa';
    },

    getSubscriptionStatus(subscription) {
      if (!subscription || !subscription.valid_until) return 'Inactiva';
      return new Date(subscription.valid_until) > new Date() ? 'Activa' : 'Expirada';
    },

    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },

    toggleSidebar() {
      this.$emit('toggle-sidebar');
    },

    logout() {
      this.$emit('logout');
    },

    showToast(message, type = 'info') {
      this.$emit('show-toast', message, type);
    }
  },
  async mounted() {
    await this.fetchUserData();
    
    // Check if device is mobile
    this.checkMobile();
    
    // Add resize event listener
    window.addEventListener('resize', this.checkMobile);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  }
};
</script>

<style scoped>
#subscription {
  height: 100%;
  background-color: #f5f5f5;
  border-radius: 0 0 8px 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  color: #333333;
  margin-left: 12px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  flex-grow: 1;
  overflow: hidden;
}

.subscription-window {
  display: flex;
  flex-grow: 1;
  width: 100%;
  height: calc(100% - 60px);
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  position: relative;
  overflow: auto;
  padding: 20px;
}

.subscription-container {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
}

.subscription-section {
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.subscription-section h2 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #a1344e;
  font-size: 22px;
  border-bottom: 1px solid rgba(161, 52, 78, 0.2);
  padding-bottom: 10px;
}

.intro-section {
  text-align: center;
  background-color: rgba(161, 52, 78, 0.1);
}

.intro-content h2 {
  color: #a1344e;
  font-size: 20px;
  line-height: 1.3;
  margin-bottom: 15px;
}

.intro-content p {
  font-size: 16px;
  margin-bottom: 10px;
  color: #333;
}

.current-subscription {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.subscription-card {
  background-color: rgba(230, 230, 230, 0.5);
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(161, 52, 78, 0.2);
  margin-bottom: 20px;
}

.subscription-row {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.subscription-row:last-child {
  border-bottom: none;
}

.subscription-label {
  width: 120px;
  font-weight: bold;
  color: #333;
}

.subscription-value {
  flex: 1;
  color: #333;
}

.subscription-benefits h3 {
  font-size: 18px;
  margin-bottom: 10px;
  color: #a1344e;
}

.subscription-benefits ul {
  padding-left: 20px;
}

.subscription-benefits li {
  margin-bottom: 8px;
  color: #333;
}

.no-subscription {
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
  padding: 20px;
}

.no-plan-message {
  font-size: 18px;
  font-weight: bold;
  color: #a1344e;
}

.free-plan-info {
  font-size: 16px;
  opacity: 0.8;
  color: #666;
}

.plans-section h3 {
  text-align: center;
  color: #a1344e;
  font-size: 20px;
  margin-bottom: 20px;
}

.plans-grid {
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 20px;
  margin-bottom: 30px;
}

.plan-card {
  background-color: rgba(230, 230, 230, 0.7);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: transform 0.3s, box-shadow 0.3s;
  max-width: 300px;
  flex: 1;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.plan-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.plan-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: #a1344e;
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  z-index: 1;
}

.plan-header {
  padding: 20px;
  padding-top: 30px;
  background-color: rgba(161, 52, 78, 0.1);
  text-align: center;
}

.plan-header h4 {
  margin: 0 0 10px 0;
  font-size: 18px;
  color: #a1344e;
}

.plan-price {
  font-size: 24px;
  font-weight: bold;
  color: #a1344e;
}

.plan-price span {
  font-size: 14px;
  opacity: 0.8;
  font-weight: normal;
}

.plan-features {
  padding: 20px;
  flex-grow: 1;
}

.plan-features ul {
  padding-left: 20px;
  margin: 0;
}

.plan-features li {
  margin-bottom: 10px;
  color: #333;
}

.plan-action {
  padding: 20px;
  text-align: center;
}

.plan-button {
  background-color: #a1344e;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  font-size: 14px;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.plan-button:hover {
  background-color: #b71c1c;
  text-decoration: none;
  color: white;
}

.premium-button {
  background-color: #a1344e;
}

.unique-button {
  background-color: #666;
}

.unique-button:hover {
  background-color: #555;
}

.separator {
  text-align: center;
  font-size: 18px;
  color: #a1344e;
  margin: 30px 0;
  font-weight: bold;
}

.paid-intro {
  text-align: center;
}

.paid-intro p {
  font-size: 16px;
  margin-bottom: 20px;
  color: #333;
}

.show-form-button {
  background-color: #f0f0f0;
  color: #a1344e;
  border: 2px solid #a1344e;
  padding: 12px 24px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 16px;
}

.show-form-button:hover {
  background-color: #a1344e;
  color: white;
}

.paid-form p {
  font-size: 16px;
  margin-bottom: 20px;
  color: #333;
}

.form-container {
  background-color: rgba(230, 230, 230, 0.5);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 15px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #333;
}

.form-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
}

.form-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.search-button {
  background-color: #a1344e;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
}

.search-button:hover:not([disabled]) {
  background-color: #b71c1c;
}

.search-button[disabled] {
  background-color: #ccc;
  cursor: not-allowed;
}

.cancel-button {
  background-color: #f0f0f0;
  color: #666;
  border: 1px solid #ddd;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.cancel-button:hover {
  background-color: #e0e0e0;
}

.help-text {
  font-size: 14px;
  text-align: center;
  color: #666;
}

.help-text a {
  color: #a1344e;
  text-decoration: none;
}

.help-text a:hover {
  text-decoration: underline;
}

.loading-subscription {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  font-size: 18px;
  color: #666;
}

@media screen and (max-width: 768px) {
  #subscription {
    margin-left: 0;
    width: 100%;
  }

  .subscription-window {
    padding: 15px;
  }

  .subscription-section {
    padding: 15px;
    margin-bottom: 20px;
  }

  .plans-grid {
    flex-direction: column;
    align-items: center;
  }

  .plan-card {
    max-width: 100%;
    width: 100%;
  }

  .form-actions {
    flex-direction: column;
  }

  .intro-content h2 {
    font-size: 18px;
  }

  .intro-content p {
    font-size: 14px;
  }
}
</style>
